import { Location } from '@angular/common';
import { Injectable, inject } from '@angular/core';

import {
    AccaBoostConfig,
    BetslipBarConfig,
    EpcotConfig,
    LayoutNavigationConfig,
    MyBetsConfig,
} from '@frontend/sports/common/client-config-data-access';
import { ComponentProxy } from '@frontend/sports/deferred/feature/component-loader';
import { SportsProductUtils } from '@frontend/sports/host-app/sports-product/feature/utils';
import { DialogAnimation, ModalDialogOptions } from '@frontend/sports/modal/feature';
import { RedirectHelperService } from '@frontend/sports/navigation/core/feature/redirect';
import { ModelPopupTypes, UrlHelperService } from '@frontend/sports/navigation/core/feature/url-helper';
import { PopupManager } from '@frontend/sports/popup/feature';
import { TrackingService, trackingConstants } from '@frontend/sports/tracking/feature';
import { OpenBetsSummary } from '@frontend/sports/types/models/my-bets';
import { UserService } from '@frontend/sports/user/feature';
import { BottomNavService, MenuAction, MenuActionsService, NavigationService } from '@frontend/vanilla/core';
import { combineLatest, switchMap } from 'rxjs';

import { BetslipIntegrationService } from '../../betslip/integration/betslip-integration.service';
import { BetColumnType } from '../bet-column/bet-column-type';
import { BetModalComponentLoaderService } from '../bet-modal/bet-modal-component-loader.service';
import { BetModalComponent } from '../bet-modal/bet-modal.component';
import { MyBetsSummaryService } from '../my-bets-base/my-bets-summary.service';
import { BottomNavClickActionsNames } from './bottom-nav-click-actions-names';

@Injectable({
    providedIn: 'root',
})
export class BottomNavActionsService {
    private vanillaNavigation = inject(NavigationService);
    private popupManager = inject(PopupManager);
    private trackingService = inject(TrackingService);
    private redirectHelper = inject(RedirectHelperService);
    private location = inject(Location);
    private myBetsSummary = inject(MyBetsSummaryService);
    private userService = inject(UserService);
    private urlHelper = inject(UrlHelperService);
    private betslipIntegrationService = inject(BetslipIntegrationService);
    private bottomNavService = inject(BottomNavService);
    private menuActionsService = inject(MenuActionsService);
    private accaBoostConfig = inject(AccaBoostConfig);
    private layoutNavigationConfig = inject(LayoutNavigationConfig);
    private epcotConfig = inject(EpcotConfig);
    private mybetsConfig = inject(MyBetsConfig);
    private betModalComponentLoaderService = inject(BetModalComponentLoaderService);
    private productUtils = inject(SportsProductUtils);

    private static readonly betslipBadgeClass: string = 'badge-primary';
    private static readonly extraIconBadgeClass: string = 'badge-extra-bubble-icon';
    private static readonly myBetsBadgeClass: string = 'badge-primary theme-spot-filled';

    private openBetsCount: number;
    private hasLiveOpenBets = false;

    betModalProxy: ComponentProxy<BetModalComponent>;
    private config = inject(BetslipBarConfig);

    constructor() {
        this.betModalProxy = this.betModalComponentLoaderService.getBetModalComponent();
    }

    init(): void {
        this.bindActions();

        if (!this.mybetsConfig.isOpenSummaryLoadingRestricted) {
            this.myBetsSummary.summaryUpdate.pipe(this.productUtils.filterSportsEmitLast()).subscribe((stats: OpenBetsSummary) => {
                this.hasLiveOpenBets = stats.liveBetsCount > 0;
                this.openBetsCount = stats.openBetsCount;

                if (this.mybetsConfig.hideBottomNavMyBetsIndicator) {
                    return;
                }

                let betsCount: number | string = 0;
                let badgeClass = BottomNavActionsService.myBetsBadgeClass;
                if (this.mybetsConfig.isShowOpenBetsCount) {
                    if (stats.openBetsCount > 99) {
                        betsCount = '99+';
                        badgeClass = badgeClass.replace('theme-spot-filled', 'open-bets bets-99-plus');
                    } else {
                        betsCount = stats.openBetsCount;
                        badgeClass = badgeClass.replace('theme-spot-filled', 'open-bets');
                    }
                } else betsCount = stats.liveBetsCount;
                const myBetsItem = this.layoutNavigationConfig?.bottomNavItemsMapping['mybets'] || 'mybets';

                this.bottomNavService.setItemCounter(myBetsItem, betsCount || null, badgeClass);
            });
        }

        this.betslipIntegrationService
            .betslipInitialized$()
            .pipe(
                this.productUtils.filterSportsEmitLast(),
                switchMap(() =>
                    combineLatest([this.betslipIntegrationService.betslipPicksCount$(), this.betslipIntegrationService.isAccaBoostToken$()]),
                ),
            )
            .subscribe(([count, isAccaBoost]) => this.setBetslipCounter(count, isAccaBoost));
    }

    betslipToggled(): void {
        if (this.config.enableIntegratedSheetView) {
            this.betModalProxy.update({ active: BetColumnType.Betslip.toString() });
            this.popupManager.open(ModelPopupTypes.BetModal, undefined, {
                data: { proxy: this.betModalProxy },
            });
        } else {
            if (this.popupManager.isOpen()) {
                this.trackingService.track(trackingConstants.EVENT_PAGE_VIEW, { [trackingConstants.PAGE_NAME]: 'M2_slip_action_ClosePopup' });
            }
            this.popupManager.open('betslip', undefined);
        }
    }

    myBetsToggled(): void {
        if (this.config.enableIntegratedSheetView) {
            if (this.userService.isAuthenticated) {
                this.betModalProxy.update({ active: BetColumnType.MyBets.toString() });
                this.popupManager.open(ModelPopupTypes.BetModal, undefined, {
                    data: { proxy: this.betModalProxy },
                });
            } else {
                const betModalUrl = this.urlHelper.getBetModalUrl();
                const betModalAbsoluteUrl = this.urlHelper.getAbsolute(betModalUrl);
                this.menuActionsService.invoke(MenuAction.GOTO_LOGIN, '', [undefined, undefined, { betModalAbsoluteUrl }]);
            }
        } else {
            const myBetsUrl = this.urlHelper.getMyBetsUrl();
            const myBetsAbsoluteUrl = this.urlHelper.getAbsolute(myBetsUrl);

            if (this.location.path() === myBetsUrl) {
                this.redirectHelper.goBack();
                this.myBetsTracking(false);
            } else {
                if (this.userService.isAuthenticated) {
                    if (this.epcotConfig.isEnabled) {
                        this.popupManager.updateDialogAnimation(ModelPopupTypes.MyBets, DialogAnimation.SlideInFromBottom);
                        const options: ModalDialogOptions<void> = {
                            settings: {
                                closeAnimation: DialogAnimation.SlideOutFromTop,
                            },
                        };
                        this.popupManager.open(ModelPopupTypes.MyBets, undefined, options);
                    } else {
                        this.vanillaNavigation.goTo(myBetsUrl);
                    }
                } else {
                    const myBetsPopupUrl = this.urlHelper.getMyBetPopupUrl();
                    const myBetsPopupAbsoluteUrl = this.urlHelper.getAbsolute(myBetsPopupUrl);
                    const returnUrl = this.epcotConfig.isEnabled ? myBetsPopupAbsoluteUrl : myBetsAbsoluteUrl;
                    this.menuActionsService.invoke(MenuAction.GOTO_LOGIN, '', [undefined, undefined, { returnUrl }]);
                }
                this.myBetsTracking(true);
            }
        }
    }

    menuToggled(): void {
        this.vanillaNavigation.goToOverlayOutlet('menu');
    }

    private myBetsTracking(isOpen: boolean): void {
        if (!isOpen) {
            return;
        }

        let pageName: string;
        if (this.hasLiveOpenBets) {
            pageName = 'HIconON_Live';
        } else if (this.openBetsCount > 0) {
            pageName = 'HIcon_Open';
        } else {
            pageName = 'HIcon_Settled';
        }
        this.trackingService.update({ [trackingConstants.PAGE_REFERRING_ACTION]: pageName });
    }

    private bindActions() {
        this.menuActionsService.register(BottomNavClickActionsNames.Betslip, () => this.betslipToggled());
        this.menuActionsService.register(BottomNavClickActionsNames.Bets, () => this.myBetsToggled());
        this.menuActionsService.register(BottomNavClickActionsNames.Menu, () => this.menuToggled());
    }

    private setBetslipCounter(count: number, isAccaBoost: boolean): void {
        this.bottomNavService.setItemCounter(
            'betslip',
            count || null,
            isAccaBoost ? this.accaBoostBadgeClass : BottomNavActionsService.betslipBadgeClass,
        );
    }

    private get accaBoostBadgeClass(): string {
        return `${BottomNavActionsService.betslipBadgeClass} ${BottomNavActionsService.extraIconBadgeClass} ${this.accaBoostConfig.icon}`;
    }
}
